import { Component, computed, ElementRef, inject, Injector, input, Input, OnDestroy, signal, type Type, viewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormGroup } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, concat, forkJoin, merge, of, Subject, type Observable } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { environment } from '@environment';
import { Constants } from '@frontendlib/common/types/Constants';
import { EventsListComponent } from '@modules/calendar-app/components/events-list/events-list.component';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { type EventsListSortField } from '@modules/calendar-app/types/events-list-state';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { type BaseStitchListComponent } from '@modules/common/components/list/base-stitch-list.component';
import { Connection } from '@modules/connections/types/connection';
import { Contact } from '@modules/contacts/types/contact';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { type ContactsListStateSortField } from '@modules/contacts/types/contacts-list-state';
import { ContactsListComponent } from '@modules/contacts/components/contacts-list/contacts-list.component';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { FilesFilters } from '@modules/files/types/files-filters';
import { type FilesListStateSortField } from '@modules/files/types/files-list-state';
import { FilesListComponent } from '@modules/files/components/files-list/files-list.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { MessagesModule } from '@modules/messages/messages.module';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { type MessagesListSortField } from '@modules/messages/types/messages-list-state';
import { Participant } from '@modules/messages/types/participant';
import { NotesModule } from '@modules/notes/notes.module';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import { type NoteListSortField } from '@modules/notes/types/notes-list-state';
import { TasksModule } from '@modules/tasks/tasks.module';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { type TaskSortableFields } from '@modules/tasks/types/tasks-list-state';
import { HUB_STITCH_TYPES, type HubStitchType } from '@sharedlib/common/types/hub-stitch-types';
import { Order } from '@sharedlib/common/types/order.dto';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { StitchType } from '@sharedlib/common/types/StitchType';
import {
  HistoryService,
  type HubSuggestionAudience,
  type HubSuggestionScope,
} from '@modules/common/services/history.service';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { contactsOwnerContext } from '@modules/contacts/services/contacts-owner-context';
import { type AppTenantScopeService } from '@modules/common/services/app-tenant-scope.service';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { TasksTenantScopeService } from '@modules/tasks/services/tasks-tenant-scope.service';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { KnowledgePanelService } from '../../../knowledge/services/knowledge-panel.service';
import { Knot } from '../../../knots/types/knot';
import { Tag } from '../../../tags/types/tag';
import { CalendarAppContextDirective } from '../../../calendar-app/directives/app-calendar-app-context.directive';
import { ContactsAppContextDirective } from '../../../contacts/directives/app-contacts-app-context.directive';
import { FilesAppContextDirective } from '../../../files/directives/app-files-app-context.directive';
import { MailAppContextDirective } from '../../../messages/directives/app-mail-app-context.directive';
import { NotesAppContextDirective } from '../../../notes/directives/app-notes-app-context.directive';
import { TaskingAppContextDirective } from '../../../tasks/directives/app-tasking-app-context.directive';
import { StitchServiceFactory } from '../../../common/factories/stitch-service.factory';
import { EntityHubResultsHeaderComponent } from './entity-hub-results-header/entity-hub-results-header.component';
import { EntityHubSortComponent } from './entity-hub-sort/entity-hub-sort.component';
import { EntityHubTabsComponent } from './entity-hub-tabs/entity-hub-tabs.component';
import { isHubSearchApplied } from './hub-search';
import {
  DEFAULT_HUB_SORT,
  hasHubFilters,
  HUB_FILTER_REREAD_DELAY_MS,
  type HubFilters,
  type HubSortField,
  NO_HUB_FILTERS,
  sameHubFilters,
} from './hub-sort';
import { HUB_TABS, type HubTab } from './hub-tabs';
import { SheetsFilters } from '@modules/sheets/types/sheets-filters';
import { type SheetListSortField } from '@modules/sheets/types/sheets-list-state';
import { SheetsAppContextDirective } from '@modules/sheets/directives/app-sheets-app-context.directive';
import { SheetsModule } from '@modules/sheets/sheets.module';

type HubTenant = { type: HubStitchType; tenant: string | null };

function audiencesOf(selections: readonly HubTenant[]): HubSuggestionAudience[] {
  const groups = new Map<string | null, HubStitchType[]>();
  for (const { type, tenant } of selections) groups.set(tenant, [...(groups.get(tenant) ?? []), type]);
  return [...groups].map(([tenant, stitchTypes]) => ({ tenant, stitchTypes }));
}

const HUB_TENANT_SCOPES: ReadonlyArray<Type<AppTenantScopeService>> = [
  MailboxTenantScopeService,
  CalendarTenantScopeService,
  TasksTenantScopeService,
  ContactsTenantScopeService,
  NotesTenantScopeService,
  FilesTenantScopeService,
];

type EntitySortFieldMap = {
  [StitchType.MESSAGE]: MessagesListSortField;
  [StitchType.EVENT]: EventsListSortField;
  [StitchType.TASK]: TaskSortableFields;
  [StitchType.CONTACT]: ContactsListStateSortField;
  [StitchType.NOTE]: NoteListSortField;
  [StitchType.FILE]: FilesListStateSortField;
  [StitchType.SHEET]: SheetListSortField;
};

@Component({
  selector: 'app-entity-hub',
  templateUrl: './entity-hub.component.html',
  styleUrl: './entity-hub.component.less',
  standalone: true,
  imports: [
    SheetsAppContextDirective,
    SheetsModule,
    MessagesModule,
    EntityHubTabsComponent,
    EntityHubSortComponent,
    EntityHubResultsHeaderComponent,
    TasksModule,
    NotesModule,
    EventsListComponent,
    ContactsListComponent,
    FilesListComponent,
    NoContentStubComponent,
    CalendarAppContextDirective,
    ContactsAppContextDirective,
    FilesAppContextDirective,
    MailAppContextDirective,
    NotesAppContextDirective,
    TaskingAppContextDirective,
  ],
  providers: [KnowledgePanelService],
})
export class EntityHubComponent implements OnDestroy {
  // Inputs
  @Input() public contextMenuStateVariant: string;
  public readonly contact = input<Contact>();

  // Filters
  protected readonly messageFilters = signal<Partial<MessagesFilters>>(undefined);
  protected readonly eventFilters = signal<Partial<EventsFilters>>(undefined);
  protected readonly taskFilters = signal<Partial<TasksFilters>>(undefined);
  protected readonly contactFilters = signal<Partial<ContactsFilters>>(undefined);
  protected readonly noteFilters = signal<Partial<NotesFilters>>(undefined);
  protected readonly sheetFilters = signal<Partial<SheetsFilters>>(undefined);
  protected readonly fileFilters = signal<Partial<FilesFilters>>(undefined);

  // Hub counts per entity type. A tab with no results shows 0, by design.
  protected readonly counts = signal<Record<string, number>>({});
  /** Identifies the query the counts on screen came from — see `applyCounts`. */
  private countsKey = '';
  /**
   * Whether `counts` answers the current query. A tab badge may read an unknown as 0, since it always shows
   * a number; the results header may not, because "No results" claims the search matched nothing.
   */
  protected readonly countsKnown = signal(false);
  public readonly form: FormGroup<EntityHubForm> = new FormGroup<EntityHubForm>({
    entityType: new FormControl<StitchType>(StitchType.MESSAGE),
    sortBy: new FormControl<HubSortField>(DEFAULT_HUB_SORT),
    pinned: new FormControl<boolean>(false),
    flagged: new FormControl<boolean>(false),
  });

  protected readonly activeType = toSignal(this.form.controls.entityType.valueChanges, {
    initialValue: this.form.controls.entityType.value,
  });

  protected readonly activeTab = computed<HubTab>(
    () => HUB_TABS.find((tab) => tab.type === this.activeType()) ?? HUB_TABS[0],
  );

  protected readonly activeSort = toSignal(this.form.controls.sortBy.valueChanges, {
    initialValue: this.form.controls.sortBy.value,
  });

  /** The Pinned / Flagged state, once per change: a reset of both lands as one emission, not two. */
  private readonly filters$: Observable<HubFilters> = this.form.valueChanges.pipe(
    startWith(undefined),
    map(() => ({
      pinned: !!this.form.controls.pinned.value,
      flagged: !!this.form.controls.flagged.value,
    })),
    distinctUntilChanged(sameHubFilters),
  );

  protected readonly activeFilters = toSignal(this.filters$, { requireSync: true });

  protected readonly filtersApplied = computed(() => hasHubFilters(this.activeFilters()));

  /** The list of the active tab, the only one of the six rendered at a time. */
  private readonly activeList = viewChild<BaseStitchListComponent<StitchItemSummaryDto>>('activeList');

  protected readonly StitchType = StitchType;
  private readonly entityHubResults = viewChild<ElementRef<HTMLDivElement>>('entityHubResults');
  private readonly alive = new Subject<void>();
  private readonly refreshCounts = new Subject<void>();
  /** Re-reads the counts alone: new filters would reset the active list and drop its page and selection. */
  private readonly rereadCounts = new Subject<void>();
  // Services
  private readonly http = inject(HttpClient);
  private readonly dynamicPanelService = inject(DynamicPanelService);
  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);
  private readonly knowledgePanelService = inject(KnowledgePanelService);
  private readonly stitchServiceFactory = inject(StitchServiceFactory);
  private readonly historyService = inject(HistoryService);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly spaceContextStore = inject(SpaceContextStore);

  /**
   * The search text, owned by the surface's `HistoryService`: the field that edits it sits above the
   * knowledge rows, outside this component, and the facet rows read it from there too. Already
   * debounced by the field.
   */
  private readonly query$ = this.historyService.onHubSearchQueryChanges();

  protected readonly searchApplied = toSignal(
    combineLatest([this.query$, this.historyService.onChanges()]).pipe(
      map(([query, record]) => isHubSearchApplied(query, record)),
    ),
    { initialValue: false },
  );

  /** The number the active tab's badge shows, so the header and the badge cannot disagree. */
  protected readonly activeCount = computed(() => this.counts()[this.activeType()] ?? 0);

  /**
   * The item the dynamic panel is previewing, by id: its card is the one the hub draws as selected.
   * The hub is the only place that knows what the panel has open; a card cannot inject the panel
   * service itself, being provided at layout level while cards also render in modals and test beds.
   */
  protected readonly previewedItemId = toSignal(
    this.dynamicPanelService.getFormItem().pipe(map((item) => item?.id ?? null)),
    { initialValue: null },
  );

  constructor() {
    this.setupFiltersResetOnContactSwitch();
    this.setupFilterSubscription();
    this.setupCountsSubscription();
    this.setupSuggestionScope();
    this.setupCountsRefreshOnStitchEvents();
    this.setupScrollResetOnTabChange();
  }

  protected selectType(type: StitchType): void {
    this.form.controls.entityType.setValue(type);
  }

  protected selectSort(sort: HubSortField): void {
    this.form.controls.sortBy.setValue(sort);
  }

  protected selectFilters(filters: HubFilters): void {
    this.form.patchValue(filters);
  }

  /**
   * Clears only what the header's action names: the search (the text and every marker, as the field's `X`
   * does), the filters, or both for Clear all.
   */
  protected clearApplied(): void {
    if (this.searchApplied()) {
      this.historyService.resetHistory();
    }
    if (this.filtersApplied()) {
      this.clearFilters();
    }
  }

  public openInDP(dto: StitchItemSummaryDto): void {
    this.stitchServiceFactory
      .getStitchItemById(dto.itemType, dto.id, dto.accountId ? { tenantItems: [dto] } : undefined)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullStitchObject) => {
        this.dynamicPanelService.setFormItem(fullStitchObject, true, undefined, 'hub');
      });
  }

  public openInModal(id: string, type: StitchType, dto?: StitchItemSummaryDto): void {
    this.stitchServiceFactory
      .getStitchItemById(type, id, dto?.accountId ? { tenantItems: [dto] } : undefined)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((item) => {
        this.modalService.openFullForm(item, this.injector);
      });
  }

  ngOnDestroy(): void {
    this.historyService.updateHubSearchQuery('');
    this.historyService.updateHubSuggestionScope(null);
    this.alive.next();
    this.alive.complete();
  }

  /**
   * The results container persists across tab switches (only its @switch-ed content is torn
   * down), so its scrollTop carries over from whatever tab was viewed before. Reset it whenever
   * the entity type changes, otherwise a leftover scroll position from a taller list can clip the
   * top of a shorter tab's content (e.g. an empty-state illustration).
   */
  private setupScrollResetOnTabChange(): void {
    this.form.controls.entityType.valueChanges.pipe(takeUntil(this.alive)).subscribe(() => {
      const results = this.entityHubResults()?.nativeElement;
      if (results) {
        results.scrollTop = 0;
      }
    });
  }

  /**
   * Sets up the filter subscription that listens to form changes and updates entity filters
   */
  private setupFilterSubscription(): void {
    combineLatest([
      // contact
      toObservable(this.contact),
      // related entity type
      this.form.controls.entityType.valueChanges.pipe(
        startWith(this.form.controls.entityType.value ?? StitchType.MESSAGE),
      ),
      // sort by
      this.form.controls.sortBy.valueChanges.pipe(
        startWith(this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc }),
      ),
      // search
      this.query$,
      // pinned / flagged
      this.filters$,
      // knowledge panel knots
      concat(of([]), this.knowledgePanelService.getSelectedKnots()),
      // knowledge panel tags
      concat(of([]), this.knowledgePanelService.getSelectedTags()),
      // knowledge panel connections
      concat(of([]), this.knowledgePanelService.getSelectedConnections()),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([currentContact, stitchType, _sort, _search, _filters, knots, tags, selectedConnections]) => {
        if (!currentContact?.id) {
          return;
        }

        this.updateEntityFilters(currentContact, stitchType, knots, tags, selectedConnections);
      });
  }

  /**
   * Sets up a subscription to fetch entity counts for all hub tabs.
   * Listens to the same inputs as the filter subscription (except entityType and sortBy)
   * and groups count requests by the audiences used by the tab lists.
   */
  private setupCountsSubscription(): void {
    combineLatest([
      toObservable(this.contact),
      this.query$,
      this.filters$,
      concat(of([] as Knot[]), this.knowledgePanelService.getSelectedKnots()),
      concat(of([] as Tag[]), this.knowledgePanelService.getSelectedTags()),
      concat(of([] as Connection[]), this.knowledgePanelService.getSelectedConnections()),
      toObservable(this.contact).pipe(switchMap((contact) => this.countTenantSelections(contact))),
      // Refresh triggers for stitch events: archive and trash moves, and pin / flag changes under a filter
      merge(this.refreshCounts, this.rereadCounts).pipe(startWith(undefined)),
    ])
      .pipe(
        // Collapse synchronous emissions from combineLatest (contact change triggers
        // multiple sources: contact signal, knots/tags/connections clearing, refresh).
        // Microtask debounce ensures only the final stable state fires the request.
        debounceTime(0),
        switchMap(
          ([currentContact, search, filters, knots, tags, selectedConnections, tenants, _refresh]: [
            Contact,
            string,
            HubFilters,
            Knot[],
            Tag[],
            Connection[],
            HubTenant[],
            unknown,
          ]) => {
            const connections = currentContact?.emails?.map((email) => email.value) ?? [];
            const hubRequiredConnections = selectedConnections.map((c) => c.name);
            const knotNames = knots.map((k) => k.name);
            const tagNames = tags.map((t) => t.name);
            const query = (search || '').trim();
            const key = this.buildCountsKey({
              contactId: currentContact?.id,
              contactOwnerId: currentContact?.accountId,
              tenantKey: tenants,
              connections,
              query,
              knotNames,
              tagNames,
              hubRequiredConnections,
              pinned: filters.pinned,
              flagged: filters.flagged,
            });

            this.clearCountsForNewQuery(key);

            if (!currentContact?.id) {
              return of({ key, counts: {} as Record<string, number> });
            }

            const params: Record<string, string | string[]> = {
              stitchedToEntityUid: currentContact.id,
              deleted: 'false',
              archived: 'false',
            };

            if (connections.length) {
              params['connections[]'] = connections;
            }
            if (hubRequiredConnections.length) {
              params['hubRequiredConnections[]'] = hubRequiredConnections;
            }
            if (knotNames.length) {
              params['knots[]'] = knotNames;
            }
            if (tagNames.length) {
              params['tags[]'] = tagNames;
            }
            if (query) {
              params['query'] = query;
            }
            if (filters.pinned) {
              params['pinned'] = 'true';
            }
            if (filters.flagged) {
              params['flagged'] = 'true';
            }

            return this.fetchTenantCounts(params, tenants).pipe(
              map((counts) => ({ key, counts })),
              catchError(() => of({ key, counts: undefined })),
            );
          },
        ),
        takeUntil(this.alive),
      )
      .subscribe(({ key, counts }) => {
        this.applyCounts(key, counts);
      });
  }

  private setupSuggestionScope(): void {
    toObservable(this.contact)
      .pipe(
        switchMap((contact): Observable<HubSuggestionScope | null> => {
          if (!contact?.id) return of(null);
          const connections = (contact.emails ?? []).map((email) => email.value);
          return this.countTenantSelections(contact).pipe(
            map((selections) => ({
              stitchedToEntityUid: contact.id,
              connections,
              audiences: audiencesOf(selections),
            })),
            startWith({ stitchedToEntityUid: contact.id, connections, audiences: [] }),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe((scope) => this.historyService.updateHubSuggestionScope(scope));
  }

  /** Known item owners override every app; otherwise each tab keeps its settled app audience. */
  private countTenantSelections(contact: Contact | undefined): Observable<HubTenant[]> {
    if (!contact?.id) return of([]);
    const owner = contact.accountId;
    if (owner) {
      return of(HUB_TABS.map(({ type }) => ({ type, tenant: owner })));
    }
    return combineLatest(
      HUB_TENANT_SCOPES.map((token, index) => {
        const scope = this.injector.get(token, null);
        const selection =
          scope?.selectedTeamId?.() ??
          this.spaceContextStore.contextId$.pipe(
            map((id) => (this.spaceContextStore.type() === SpaceType.SHARED_SPACE ? id : null)),
          );
        return selection.pipe(map((tenant) => ({ type: HUB_TABS[index].type, tenant })));
      }),
    );
  }

  /** Share one request when audiences match, and take each tab's count only from its own audience. */
  private fetchTenantCounts(
    params: Record<string, string | string[]>,
    tenants: HubTenant[],
  ): Observable<Record<string, number>> {
    return forkJoin(
      audiencesOf(tenants).map(({ tenant, stitchTypes }) =>
        this.http
          .get<
            Record<string, number>
          >(`${environment.baseUrl}/api/search/hub-counts`, { params, context: contactsOwnerContext(undefined, tenant) })
          .pipe(
            map((counts) =>
              Object.fromEntries(stitchTypes.filter((type) => type in counts).map((type) => [type, counts[type]])),
            ),
          ),
      ),
    ).pipe(map((counts) => Object.assign({}, ...counts)));
  }

  /**
   * Identifies a counts request by everything that changes what it asks for. The contact's own
   * addresses belong here as much as its id: two contacts can share neither, but one contact can
   * have its addresses edited while keeping the same id, and those counts describe a different
   * question.
   */
  private buildCountsKey(query: {
    contactId: string | undefined;
    contactOwnerId?: string;
    tenantKey?: HubTenant[];
    connections: string[];
    query: string;
    knotNames: string[];
    tagNames: string[];
    hubRequiredConnections: string[];
    pinned: boolean;
    flagged: boolean;
  }): string {
    return JSON.stringify([
      query.contactId ?? '',
      query.contactOwnerId ?? '',
      query.tenantKey ?? [],
      query.connections,
      query.query,
      query.knotNames,
      query.tagNames,
      query.hubRequiredConnections,
      query.pinned,
      query.flagged,
    ]);
  }

  /**
   * Until a request lands, the numbers on screen still answer the question before it. A changed
   * contact, search, marker set or filter therefore starts from unknown — zeroes, the way every other
   * unknown reads — rather than showing one contact's counts under another. A refresh of the same
   * query keeps its numbers, so a stitch event never blinks the row.
   */
  private clearCountsForNewQuery(key: string): void {
    if (key !== this.countsKey) {
      this.counts.set({});
      this.countsKey = key;
      this.countsKnown.set(false);
    }
  }

  /**
   * A failed request keeps the numbers already on screen, but only while they still describe the
   * same query. Once the contact, the search, the markers or the filters change, the previous counts belong to
   * something the lists are no longer showing, so they are cleared rather than misattributed.
   */
  private applyCounts(key: string, counts: Record<string, number> | undefined): void {
    if (counts) {
      this.counts.set(counts);
      this.countsKey = key;
      this.countsKnown.set(true);
      return;
    }

    if (key !== this.countsKey) {
      this.counts.set({});
      this.countsKey = key;
      this.countsKnown.set(false);
    }
  }

  /**
   * Sets up listeners for stitch events that should trigger a counts refresh.
   * Listens to ARCHIVED, UNARCHIVED, MOVED_TO_TRASH, RESTORED_FROM_TRASH events
   * for all hub entity types. Rebuilds event streams when space context changes.
   *
   * Also listens to UPDATED events that change what an applied filter keeps, debounced like the lists'
   * own reload so an unpinned item leaves the list and the badges together. PINNED / FLAGGED reach only
   * the tab that acted; UPDATED reaches every session.
   */
  private setupCountsRefreshOnStitchEvents(): void {
    const eventTypes = [
      SignalStitchEventType.ARCHIVED,
      SignalStitchEventType.UNARCHIVED,
      SignalStitchEventType.MOVED_TO_TRASH,
      SignalStitchEventType.RESTORED_FROM_TRASH,
    ];

    const hubStitchTypes = [
      StitchType.MESSAGE,
      StitchType.EVENT,
      StitchType.TASK,
      StitchType.CONTACT,
      StitchType.NOTE,
      StitchType.FILE,
    ];

    // Rebind to the open contact's owner, including an owner-only change of the same contact id.
    combineLatest([this.spaceContextStore.contextId$, toObservable(this.contact)])
      .pipe(
        switchMap(([contextId, contact]) =>
          this.countTenantSelections(contact).pipe(
            map((tenants) => [
              ...new Set(tenants.map(({ tenant }) => tenant ?? contextId).filter((id): id is string => !!id)),
            ]),
          ),
        ),
        filter((contextIds) => contextIds.length > 0),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        // switchMap automatically unsubscribes from previous inner observable
        switchMap((contextIds) => {
          const eventStreams = contextIds.flatMap((contextId) =>
            hubStitchTypes.map((stitchType) => this.stitchEventsService.getEvents(contextId, stitchType, eventTypes)),
          );
          const filteredFieldChanges = merge(
            ...contextIds.flatMap((contextId) =>
              HUB_STITCH_TYPES.map((stitchType) =>
                this.stitchEventsService
                  .getEvents(contextId, stitchType, SignalStitchEventType.UPDATED)
                  .pipe(filter((event) => this.changesAppliedFilter(event.changes))),
              ),
            ),
          );
          // Merge all streams and debounce to avoid multiple refreshes for bulk operations
          return merge(
            merge(...eventStreams).pipe(
              debounceTime(300),
              tap(() => this.refreshCounts.next()),
            ),
            filteredFieldChanges.pipe(
              debounceTime(Constants.DEBOUNCE_EVENT_LIST_RELOAD),
              tap(() => this.rereadCounts.next()),
            ),
            filteredFieldChanges.pipe(
              debounceTime(HUB_FILTER_REREAD_DELAY_MS),
              tap(() => this.reloadActiveList()),
              tap(() => this.rereadCounts.next()),
            ),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe();
  }

  private reloadActiveList(): void {
    const list = this.activeList();
    list?.loadPage(list.page);
  }

  private changesAppliedFilter(
    changes: { readonly pinned?: boolean; readonly flagged?: boolean } | undefined,
  ): boolean {
    return (
      (!!this.form.controls.pinned.value && changes?.pinned !== undefined) ||
      (!!this.form.controls.flagged.value && changes?.flagged !== undefined)
    );
  }

  /** Opening another contact starts its hub unfiltered, as it starts with no search; the sort carries over. */
  private setupFiltersResetOnContactSwitch(): void {
    toObservable(this.contact)
      .pipe(
        map((contact) => contact?.id),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.alive),
      )
      .subscribe(() => this.clearFilters());
  }

  private clearFilters(): void {
    this.form.patchValue(NO_HUB_FILTERS);
  }

  /**
   * Updates the appropriate entity filter based on the selected entity type
   * @param currentContact - The current contact
   * @param stitchType - The type of entity to display
   * @param knots - Selected knots from the knowledge panel
   * @param tags - Selected tags from the knowledge panel
   * @param selectedConnections - Selected connections from the knowledge panel
   */
  private updateEntityFilters(
    currentContact: Contact,
    stitchType: StitchType,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): void {
    switch (stitchType) {
      case StitchType.MESSAGE:
        this.messageFilters.set(this.getMessageFilters(currentContact, knots, tags, selectedConnections));
        break;
      case StitchType.EVENT:
        this.eventFilters.set(this.getEventFilters(currentContact, knots, tags, selectedConnections));
        break;
      case StitchType.TASK:
        this.taskFilters.set(this.getTaskFilters(currentContact, knots, tags, selectedConnections));
        break;
      case StitchType.CONTACT:
        this.contactFilters.set(this.getContactFilters(currentContact, knots, tags, selectedConnections));
        break;
      case StitchType.NOTE:
        this.noteFilters.set(this.getNoteFilters(currentContact, knots, tags, selectedConnections));
        break;
      case StitchType.FILE:
        this.fileFilters.set(this.getFileFilters(currentContact, knots, tags, selectedConnections));
        break;
      case StitchType.SHEET:
        this.sheetFilters.set(this.getSheetFilters(currentContact, knots, tags, selectedConnections));
        break;
      default:
        console.warn(`Unsupported entity type ${stitchType}`);
    }
  }

  private getMessageFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<MessagesFilters> {
    const participants = this.getContactEmails(currentContact);
    const query = this.currentQuery();
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.MESSAGE, sortConfig);

    return new MessagesFilters({
      archived: false,
      deleted: false,
      spam: false,
      recipients: participants,
      senders: participants,
      cc: participants,
      bcc: participants,
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      query,
      ...this.filterTerms(),
    });
  }

  private getEventFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<EventsFilters> {
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.EVENT, sortConfig);

    return new EventsFilters({
      archived: false,
      deleted: false,
      attendees: this.getContactEmails(currentContact),
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      query: this.currentQuery(),
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      ...this.filterTerms(),
    });
  }

  private getTaskFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<TasksFilters> {
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.TASK, sortConfig);

    return new TasksFilters({
      archived: false,
      deleted: false,
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      query: this.currentQuery(),
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      ...this.filterTerms(),
    });
  }

  private getContactFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<ContactsFilters> {
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.CONTACT, sortConfig);

    return new ContactsFilters({
      archived: false,
      deleted: false,
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      query: this.currentQuery(),
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      ...this.filterTerms(),
    });
  }

  private getNoteFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<NotesFilters> {
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.NOTE, sortConfig);

    return new NotesFilters({
      archived: false,
      deleted: false,
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      query: this.currentQuery(),
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      ...this.filterTerms(),
    });
  }

  private getSheetFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<SheetsFilters> {
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.SHEET, sortConfig);

    return new SheetsFilters({
      archived: false,
      deleted: false,
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      query: this.currentQuery(),
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      ...this.filterTerms(),
    });
  }

  private getFileFilters(
    currentContact: Contact,
    knots: Knot[],
    tags: Tag[],
    selectedConnections: Connection[],
  ): Partial<FilesFilters> {
    const sortConfig = this.form.controls.sortBy.value ?? { sortBy: 'date', sortOrder: Order.desc };
    const sortField = this.resolveSortField(StitchType.FILE, sortConfig);

    return new FilesFilters({
      archived: false,
      deleted: false,
      connections: currentContact.emails.map((email) => new Connection({ name: email.value })),
      hubRequiredConnections: selectedConnections,
      query: this.currentQuery(),
      hubMode: true,
      stitchedToEntityUid: currentContact.id,
      sortBy: sortField,
      sortOrder: sortConfig.sortOrder,
      knots,
      tags,
      ...this.filterTerms(),
    });
  }

  private getContactEmails(contact: Contact): Pick<Participant, 'name' | 'address'>[] {
    return contact.emails.map((email) => ({ name: contact.title, address: email.value }));
  }

  private currentQuery(): string {
    return this.historyService.getHubSearchQuery().trim();
  }

  /**
   * `true` for each filter that is on, and no key at all for one that is off: `false` would ask the list
   * for only the unpinned or unflagged items.
   */
  private filterTerms(): { pinned?: true; flagged?: true } {
    const terms: { pinned?: true; flagged?: true } = {};
    if (this.form.controls.pinned.value) {
      terms.pinned = true;
    }
    if (this.form.controls.flagged.value) {
      terms.flagged = true;
    }

    return terms;
  }

  /**
   * Centralized method to resolve generic sort field to entity-specific sort field
   * @param entityType - The type of entity (message, event, task, etc.)
   * @param sortField - The generic HubSortField object
   * @returns The entity-specific sort field
   */
  private resolveSortField<T extends keyof EntitySortFieldMap>(
    entityType: T,
    sortField: HubSortField,
  ): EntitySortFieldMap[T] {
    const { sortBy } = sortField;

    // title is common across all entity types
    if (sortBy === 'title') {
      return sortBy as EntitySortFieldMap[T];
    }

    // Map 'date' to the entity-specific date field
    switch (entityType) {
      case StitchType.MESSAGE:
        return 'date' as EntitySortFieldMap[T];
      case StitchType.EVENT:
        return 'startTime' as EntitySortFieldMap[T];
      case StitchType.TASK:
        return 'date' as EntitySortFieldMap[T];
      case StitchType.CONTACT:
      case StitchType.NOTE:
      case StitchType.FILE:
      case StitchType.SHEET:
        return 'createdAt' as EntitySortFieldMap[T];
      default:
        return 'createdAt' as EntitySortFieldMap[T];
    }
  }
}

interface EntityHubForm {
  entityType: FormControl<StitchType>;
  sortBy: FormControl<HubSortField>;
  pinned: FormControl<boolean>;
  flagged: FormControl<boolean>;
}
